import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { Icon, IconContainer } from "./styledComponents";
import SearchIcon from "../../assets/svg/search_icon.svg";
import { ElementContainer } from "../containers/styledComponents";
import { lightGreyOutline, lightGreyText } from "../../styles";
import { Filter as Option } from "../../types/PageFilterTypes";
import { FilterList } from "./PageFilter/FilterList";
import { MESSAGES } from "../../messages";
import _ from "lodash";

const SearchBarContainer = styled.div`
  position: relative;
`;

const StyledInput = styled.input`
  width: 100%;
  max-width: 200px;
  min-width: 150px;
  padding: 1px;
  border: none;
  font-size: 13px;
  font-family: "Apercu", sans-serif;

  &::placeholder {
    color: ${lightGreyText};
    opacity: 1;
    font-size: 14px;
  }
`;

const DropdownList = styled.div<{ hasIcon: boolean; small?: boolean }>`
  position: absolute;
  top: ${({ hasIcon, small }): string =>
    `${small ? 43 : 50 + (hasIcon ? 7 : 0)}px`};

  width: auto;
  z-index: 1000;
  border-radius: 7px;
  border: 1px solid ${lightGreyOutline};
  background-color: white;
  max-height: 50vh;
  overflow-y: auto;
  ${({ hasIcon }): string | undefined => (hasIcon ? "" : "left: -40px")}
`;

export const SearchBar = ({
  options,
  placeholder,
  onSelectOption,
  onSelectOptions,
  icon = true,
  isMultiSelect = false,
  initialSelectedOptions = [],
  small = false,
}: SearchBarProps): JSX.Element => {
  const SearchBarRef = useRef<HTMLDivElement>(null);
  const [inputValue, setInputValue] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState<Option[]>(
    initialSelectedOptions
  );
  const [filteredOptions, setFilteredOptions] = useState<Option[]>(options);

  const handleDropdownClick = (e: React.MouseEvent): void => {
    e.stopPropagation();
  };

  const getFilteredOptions = (
    options: Option[],
    searchTerm: string,
    isAsc = false
  ): Option[] => {
    const filteredOptions = options.filter(
      (option) =>
        option.displayValue.toLowerCase().includes(searchTerm) ||
        doesOptionExist(selectedOptions, option)
    );

    // Convert boolean to number for TypeScript compliance in arithmetic operations
    filteredOptions.sort(
      (a, b) =>
        +doesOptionExist(selectedOptions, isAsc ? a : b) -
        +doesOptionExist(selectedOptions, isAsc ? b : a)
    );

    return filteredOptions;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setInputValue(e.target.value);
    const searchTerm = e.target.value.toLowerCase();
    setFilteredOptions(getFilteredOptions(options, searchTerm, true));
  };

  const handleSearchBarClick = (): void => {
    setIsOpen(true);
    setFilteredOptions(getFilteredOptions(options, inputValue));
  };

  const handleSelectOption = (option: Option): void => {
    onSelectOption && !isMultiSelect && onSelectOption(option);

    isMultiSelect && handleMultiSelect(option);

    !isMultiSelect && setIsOpen(false); // Close dropdown after selection
  };

  const doesOptionExist = (
    options: Option[],
    targetOption: Option
  ): boolean => {
    return options.some((opt) => _.isEqual(opt, targetOption));
  };

  const handleMultiSelect = (option: Option): void => {
    setHasChanges(true);
    let updatedSelectedOptions: Option[] = [];

    if (doesOptionExist(selectedOptions, option)) {
      updatedSelectedOptions = selectedOptions.filter(
        (item) => item.key !== option.key
      );
    } else {
      if (option.displayValue !== MESSAGES.NO_FILTER_APPLIED) {
        updatedSelectedOptions = [...selectedOptions, option];
      } else {
        updatedSelectedOptions = [];
      }
    }

    // Update the state with the new list of selected options
    setSelectedOptions(updatedSelectedOptions);

    !isMultiSelect && setInputValue("");
  };

  useEffect(() => {
    setFilteredOptions(options);
    setSelectedOptions(initialSelectedOptions);
  }, [options]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (
        SearchBarRef.current &&
        !SearchBarRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      window.addEventListener("click", handleClickOutside);
    }

    if (!isOpen && hasChanges && onSelectOptions) {
      onSelectOptions(selectedOptions, options[0].selectorName);
      setHasChanges(false);
      setInputValue("");
    }

    return () => {
      window.removeEventListener("click", handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    setIsOpen(inputValue.length > 0);
  }, [inputValue]);

  return (
    <SearchBarContainer>
      <ElementContainer
        ai="center"
        ref={SearchBarRef}
        onClick={handleSearchBarClick}
      >
        {icon && (
          <IconContainer p={1} m={1} size={35}>
            <Icon src={SearchIcon} />
          </IconContainer>
        )}
        <StyledInput
          type="text"
          value={inputValue}
          onChange={handleChange}
          placeholder={
            selectedOptions.length
              ? selectedOptions.length > 1
                ? `${selectedOptions.length} selected`
                : selectedOptions[0].displayValue
              : placeholder
          }
        />
        {isOpen && (
          <DropdownList
            hasIcon={icon}
            onClick={handleDropdownClick}
            small={small}
          >
            {filteredOptions.map((option, index) => (
              <FilterList
                key={`${index}-selection-item-container-${option.key}`}
                option={option}
                handleSelectOption={handleSelectOption}
                isMultiSelect={isMultiSelect}
                checked={doesOptionExist(selectedOptions, option)}
              />
            ))}
          </DropdownList>
        )}
      </ElementContainer>
    </SearchBarContainer>
  );
};

type SearchBarProps = {
  options: Option[];
  isMultiSelect?: boolean;
  onSelectOption?: (option: Option) => void;
  onSelectOptions?: (options: Option[], selectorName?: string) => void;
  placeholder: string;
  isLoading?: boolean;
  icon?: boolean;
  initialSelectedOptions?: Option[];
  small?: boolean;
};
